import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import './ZoneEditor.css';

const ZoneEditor = ({ show }) => {
  const [zoneData, setZoneData] = useState(null);
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedRow, setSelectedRow] = useState(1);
  const [selectedSeat, setSelectedSeat] = useState(0);
  const [templateId, setTemplateId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        if (!show || !show.template) return;
       

        const token = localStorage.getItem("token");

        const res = await axios.get(`/templates/${show.template}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const template = res.data.oneTemplate || res.data.zoneTemplate || res.data;

        

        setTemplateId(template.id);

        // --- Procesamos location respetando x/y de los seats ---
        const location = (template.location || []).map(div => ({
          ...div,
          division: div.division ?? "División 1",
          divisionImageUrl: div.divisionImageUrl || null,
          divisionImageWidth: div.divisionImageWidth || null,
          divisionImageHeight: div.divisionImageHeight || null,
          generalPrice: Number(div.generalPrice ?? 0),
          rows: (div.rows || []).map((row, rowIndex) => ({
            ...row,
            row: Number(row.row ?? rowIndex + 1), // fila numérica
            rowPrice: Number(row.rowPrice ?? div.generalPrice ?? 0),
            seats: (row.seats || []).map((s, seatIndex) => ({
              id: Number(s.id ?? seatIndex + 1),
              x: Number(s.x), // ✅ usar valor real del template
              y: Number(s.y), // ✅ usar valor real del template
              taken: Boolean(s.taken ?? false),
              seatPrice: Number(s.seatPrice ?? row.rowPrice ?? div.generalPrice),
            })),
          })),
        }));


        

        // --- Inicializamos zoneData ---
        setZoneData({
          zoneName: template.zoneName || show.template,
          zoneImageUrl: template.zoneImageUrl || null,
          zoneImageWidth: template.zoneImageWidth || null,
          zoneImageHeight: template.zoneImageHeight || null,
          generalTicket: show.isGeneral ?? template.generalTicket ?? true,
          presentation: show.presentation?.[0] || template.presentation || { date: "", time: { start: "", end: "" }, performance: 1 },
          location,
        });

        // --- Selección inicial basada en template real ---
        if (location.length > 0) {
          setSelectedDivision(location[0].division);
          setSelectedRow(location[0].rows[0]?.row ?? 1);
          setSelectedSeat(location[0].rows[0]?.seats[0]?.id ?? 1);
        }

      } catch (err) {
        console.error("❌ Error cargando template:", err);
        Swal.fire("Error", "No se pudo cargar el template del show", "error");
      }
    };

    fetchTemplate();
  }, [show]);




  if (!zoneData) return <div>Cargando template...</div>;

  // --- Handlers ---
  const handleInputChange = (field, value) =>
    setZoneData({ ...zoneData, [field]: value });

  const handlePresentationChange = (field, value) =>
    setZoneData({
      ...zoneData,
      presentation: { ...zoneData.presentation, [field]: value },
    });

  const handleTimeChange = (field, value) =>
    setZoneData({
      ...zoneData,
      presentation: {
        ...zoneData.presentation,
        time: { ...zoneData.presentation.time, [field]: value },
      },
    });

  const handleLocationChange = (division, field, value) =>
    setZoneData({
      ...zoneData,
      location: zoneData.location.map(loc =>
        loc.division === division ? { ...loc, [field]: Number(value) } : loc
      ),
    });

  const handleRowPriceChange = (division, row, value) =>
    setZoneData({
      ...zoneData,
      location: zoneData.location.map(loc =>
        loc.division === division
          ? {
            ...loc,
            rows: loc.rows.map(r =>
              r.row === row
                ? { ...r, rowPrice: Number(value), seats: r.seats.map(s => ({ ...s, seatPrice: Number(value) })) }
                : r
            ),
          }
          : loc
      ),
    });

  const handleSeatPriceChange = (division, row, seatId, value) =>
    setZoneData({
      ...zoneData,
      location: zoneData.location.map(loc =>
        loc.division === division
          ? {
            ...loc,
            rows: loc.rows.map(r =>
              r.row === row
                ? {
                  ...r,
                  seats: r.seats.map(s =>
                    s.id === seatId ? { ...s, seatPrice: Number(value) } : s
                  ),
                }
                : r
            ),
          }
          : loc
      ),
    });

  const handleDivisionChange = (division) => {
    setSelectedDivision(division);
    const firstRow = zoneData.location.find(loc => loc.division === division)?.rows?.[0]?.row || 1;
    const firstSeat = zoneData.location.find(loc => loc.division === division)?.rows?.[0]?.seats?.[0]?.id || 0;
    setSelectedRow(firstRow);
    setSelectedSeat(firstSeat);
  };

  const handleRowSelectChange = (row) => {
    setSelectedRow(Number(row));
    const firstSeat = zoneData.location.find(loc => loc.division === selectedDivision)?.rows?.find(r => r.row === Number(row))?.seats?.[0]?.id || 0;
    setSelectedSeat(firstSeat);
  };

  const handleSeatSelectChange = (seatId) => setSelectedSeat(Number(seatId));

  // --- Guardar cambios ---
 const saveChanges = async () => {
  try {
    const zoneId = zoneData.id || null; // si ya existe, usamos su id

    // --- Preparamos location con precios y ahora también imágenes ---
    const updatesToSend = {
      zoneImageUrl: zoneData.zoneImageUrl || null,
      zoneImageWidth: zoneData.zoneImageWidth || null,
      zoneImageHeight: zoneData.zoneImageHeight || null,
      generalTicket: zoneData.generalTicket, // 👈 ESTO FALTA
      presentation: zoneData.presentation,
      location: zoneData.location.map(div => ({
        division: div.division,
        generalPrice: div.generalPrice,
        divisionImageUrl: div.divisionImageUrl || null,
        divisionImageWidth: div.divisionImageWidth || null,
        divisionImageHeight: div.divisionImageHeight || null,
        rows: zoneData.generalTicket
          ? []
          : div.rows.map(row => ({
              row: row.row,
              rowPrice: row.rowPrice,
              seats: row.seats.map(seat => ({
                id: seat.id,
                seatPrice: seat.seatPrice
              }))
            }))
      }))
    };

    const dataToSend = {
      showId: show.id,
      zoneId,
      templateId: templateId || null,
      zoneName: zoneData.zoneName,
      updates: updatesToSend,
    };

    

    const response = await axios.post("/zones/add", dataToSend, {
      headers: { "Content-Type": "application/json" },
    });

    if (response.status === 200) {
      Swal.fire("¡Éxito!", "Los datos se guardaron correctamente.", "success");
      navigate("/");
    }

  } catch (error) {
    console.error("❌ Error al guardar:", error.response?.data || error.message);
    Swal.fire(
      "Error",
      error.response?.data?.message || "Ocurrió un error inesperado",
      "error"
    );
  }
};


  return (
    <div className="zone-editor-container">
      <h1>Editor de Zona - {zoneData.zoneName}</h1>

      <div className="input-group">
        <label>Nombre de la Zona:</label>
        <input type="text" value={zoneData.zoneName} disabled />
      </div>

      <div className="input-group">
        <label>Precio General:</label>
        <input
          type="checkbox"
          checked={zoneData.generalTicket}
          onChange={(e) => handleInputChange("generalTicket", e.target.checked)}
        />
      </div>

      <div className="input-group">
        <label>Fecha Presentación:</label>
        <input
          type="date"
          value={zoneData.presentation?.date || ""}
          onChange={(e) => handlePresentationChange("date", e.target.value)}
        />
      </div>

      <div className="input-group">
        <label>Hora Inicio:</label>
        <input
          type="time"
          value={zoneData.presentation?.time?.start || ""}
          onChange={(e) => handleTimeChange("start", e.target.value)}
        />
      </div>

      <div className="input-group">
        <label>Hora Fin:</label>
        <input
          type="time"
          value={zoneData.presentation?.time?.end || ""}
          onChange={(e) => handleTimeChange("end", e.target.value)}
        />
      </div>

      <div className="input-group">
        <label>Performance:</label>
        <input
          type="number"
          value={zoneData.presentation?.performance || 1}
          min="1"
          onChange={(e) => handlePresentationChange("performance", Number(e.target.value))}
        />
      </div>

      <h3>Divisiones</h3>
      {zoneData.location.length === 0 ? (
        <p>No hay divisiones disponibles en este template.</p>
      ) : (
        <>
          <div className="input-group">
            <label>División:</label>
            <select value={selectedDivision} onChange={(e) => handleDivisionChange(e.target.value)}>
              {zoneData.location.map(div => (
                <option key={div.division} value={div.division}>{div.division}</option>
              ))}
            </select>
          </div>

          <div className="input-group">
            <label>Fila:</label>
            <select
              value={selectedRow}
              onChange={(e) => handleRowSelectChange(e.target.value)}
              disabled={zoneData.generalTicket}
            >
              {zoneData.location.find(div => div.division === selectedDivision)?.rows?.map(row => (
                <option key={row.row} value={row.row}>{row.row}</option>
              ))}
            </select>
          </div>

        
          <div className="input-group">
            <label>Precio por División:</label>
            <input
              type="number"
              value={zoneData.location.find(div => div.division === selectedDivision)?.generalPrice || 0}
              onChange={(e) => handleLocationChange(selectedDivision, "generalPrice", e.target.value)}
            />
          </div>

          {!zoneData.generalTicket && (
            <>
              <div className="input-group">
                <label>Precio por Fila:</label>
                <input
                  type="number"
                  value={zoneData.location
                    .find(div => div.division === selectedDivision)
                    ?.rows.find(r => r.row === selectedRow)?.rowPrice || 0}
                  onChange={(e) => handleRowPriceChange(selectedDivision, selectedRow, e.target.value)}
                />
              </div>

            </>
          )}
        </>
      )}

      <button onClick={saveChanges}>Guardar Cambios</button>
    </div>
  );
};

export default ZoneEditor;
